import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, CircularProgress, Collapse, IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReplayIcon from '@mui/icons-material/Replay';
import SendIcon from '@mui/icons-material/Send';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { useTranslation } from 'react-i18next';
import countingService, { DiscrepancyReport } from '../../services/countingService';
import WmsNotification, { useWmsNotification } from '../../shared/WmsNotification';
import StatisticalAccuracyPanel from '../../shared/StatisticalAccuracyPanel';

interface CountReviewProps {
  taskId: string;
  onRecount: (discrepancyLocationIds: string[]) => void;
  onSubmit: (report: DiscrepancyReport) => void;
  onRequestCancel?: () => void;
}

const CountReview: React.FC<CountReviewProps> = ({ taskId, onRecount, onSubmit, onRequestCancel }) => {
  const { t } = useTranslation('warehouse');
  const [report, setReport] = useState<DiscrepancyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMatches, setShowMatches] = useState(false);
  const { notification, notify, clear } = useWmsNotification();

  useEffect(() => {
    (async () => {
      try {
        const data = await countingService.getDiscrepancyReport(taskId);
        setReport(data);
      } catch {
        notify({ type: 'error', message: t('screens.counting.review.loadError') });
      }
      setLoading(false);
    })();
  }, [taskId, notify, t]);

  const handleRecount = useCallback(() => {
    if (!report) return;
    const locationIds = Array.from(new Set(report.discrepancies.map(d => d.locationId)));
    onRecount(locationIds);
  }, [report, onRecount]);

  const handleSubmit = useCallback(async () => {
    if (!report) return;
    try {
      await countingService.completeTask(taskId);
      onSubmit(report);
    } catch {
      notify({ type: 'error', message: t('screens.counting.review.completeFailed') });
    }
  }, [report, taskId, onSubmit, notify, t]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{t('screens.counting.review.reportLoadError')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <WmsNotification notification={notification} onClose={clear} />


      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {t('screens.counting.review.title', { taskNumber: report.taskNumber })}
      </Typography>

      <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t('screens.counting.review.summary')}</Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Typography>{t('screens.counting.review.countedLocations')} <strong>{report.totalLocations}</strong></Typography>
          <Typography>
            {t('screens.counting.review.withDiscrepancy')} <strong style={{ color: report.locationDiscrepancyCount ? '#ef5350' : 'inherit' }}>
              {report.locationDiscrepancyCount ?? report.discrepancies.length}
            </strong>
          </Typography>
          <Typography>{t('screens.counting.review.noDiscrepancy')} <strong style={{ color: '#4caf50' }}>
            {report.locationMatchCount ?? report.matches.length}
          </strong></Typography>
        </Box>
      </Paper>

      {report.statisticalAccuracy && (
        <StatisticalAccuracyPanel data={report.statisticalAccuracy} />
      )}

      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {report.discrepancies.length > 0 && (
          <Paper sx={{ bgcolor: 'background.paper', borderRadius: 2, overflow: 'auto' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main' }}>
                {t('screens.counting.review.discrepancyReport', { count: report.discrepancies.length })}
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('screens.counting.review.colLocation')}</TableCell>
                  <TableCell>{t('screens.counting.review.colProduct')}</TableCell>
                  <TableCell align="center">{t('screens.counting.review.colSystem')}</TableCell>
                  <TableCell align="center">{t('screens.counting.review.colCount')}</TableCell>
                  <TableCell align="center">{t('screens.counting.review.colDiff')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.discrepancies.map(d => (
                  <TableRow key={d.resultId} sx={{ bgcolor: 'rgba(239,83,80,0.08)' }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12 }}>
                      {d.locationCode}
                    </TableCell>
                    <TableCell>{d.isEmpty ? t('screens.counting.review.emptyLocation') : (d.productName || '-')}</TableCell>
                    <TableCell align="center">{d.systemQuantity}</TableCell>
                    <TableCell align="center">{d.countedQuantity}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${d.difference > 0 ? '+' : ''}${d.difference}`}
                        size="small"
                        color={d.difference > 0 ? 'info' : 'error'}
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        {report.matches.length > 0 && (
          <Paper sx={{ bgcolor: 'background.paper', borderRadius: 2, overflow: 'auto' }}>
            <Box sx={{ p: 2, pb: 0, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={() => setShowMatches(!showMatches)}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'success.main' }}>
                {t('screens.counting.review.matchReport', { count: report.matches.length })}
              </Typography>
              <IconButton size="small">
                {showMatches ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={showMatches}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('screens.counting.review.colLocation')}</TableCell>
                    <TableCell>{t('screens.counting.review.colProduct')}</TableCell>
                    <TableCell align="center">{t('screens.counting.review.colSystem')}</TableCell>
                    <TableCell align="center">{t('screens.counting.review.colCount')}</TableCell>
                    <TableCell align="center">{t('screens.counting.review.colDiff')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.matches.map(m => (
                    <TableRow key={m.resultId}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{m.locationCode}</TableCell>
                      <TableCell>{m.productName || '-'}</TableCell>
                      <TableCell align="center">{m.systemQuantity}</TableCell>
                      <TableCell align="center">{m.countedQuantity}</TableCell>
                      <TableCell align="center">
                        <Chip label="0 ✓" size="small" color="success" sx={{ fontWeight: 700 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </Paper>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {report.discrepancies.length > 0 && (
            <Button variant="outlined" startIcon={<ReplayIcon />} onClick={handleRecount} size="large">
              {t('screens.counting.review.recountBtn')}
            </Button>
          )}
          {onRequestCancel && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<ReportProblemIcon />}
              onClick={onRequestCancel}
              size="large"
            >
              {t('screens.counting.cancelRequest.reportBtn')}
            </Button>
          )}
        </Box>
        <Button variant="contained" color="success" size="large" startIcon={<SendIcon />}
          onClick={handleSubmit} sx={{ minWidth: 220, fontSize: 16, fontWeight: 700 }}>
          {t('screens.counting.review.submitBtn')}
        </Button>
      </Box>
    </Box>
  );
};

export default CountReview;
